import React, { useEffect, useMemo, useState } from 'react';
import {
  CustomerStatCard,
  QuickActions,
  UpcomingDeliveries,
  OutstandingInvoices,
  AIReorderSuggestions,
  ActiveDeals,
} from './components';
import {
  CustomerStatData,
  DeliveryItem,
  InvoiceItem,
  ReorderProduct,
  DealItem,
} from './customerDashboardTypes';
import { AsyncBoundary } from '../../../components/AsyncBoundary';
import { getMyCustomerProfile, Customer } from '../../../services/customersApi';
import { listMyCustomerInvoices, Invoice } from '../../../services/invoicesApi';
import { listMyCustomerDeliveries } from '../../../services/deliveriesApi';
import { formatOrderId } from '../../../utils/formatId';
import { DeliveryStop } from '../../../services/deliveriesApi';
import { listMyCustomerOrders, getSuggestedReorder, SuggestedReorderLine } from '../../../services/ordersApi';
import { Order } from '../../../services/ordersApi';
import { listPromotions, Promotion } from '../../../services/promotionsApi';
import { getSku } from '../../../services/skusApi';
import { useCartStore } from '../../../store/cartStore';
import styles from './CustomerDashboard.module.css';

function fmtMoney(n: number, signed = false): string {
  const abs = Math.abs(n);
  const formatted = `$${abs.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return n < 0 ? `-${formatted}` : signed ? `+${formatted}` : formatted;
}

function relativeDate(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  const diffDays = Math.floor((d.getTime() - today.setHours(0, 0, 0, 0)) / 86_400_000);
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  if (diffDays === 0) return `Today, ${time}`;
  if (diffDays === 1) return `Tomorrow, ${time}`;
  return `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}, ${time}`;
}

const CustomerDashboard: React.FC = () => {
  const [profile, setProfile] = useState<Customer | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [stops, setStops] = useState<DeliveryStop[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [suggestions, setSuggestions] = useState<SuggestedReorderLine[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reorderToast, setReorderToast] = useState<string | null>(null);
  const addToCart = useCartStore((s) => s.addItem);

  const load = () => {
    setLoading(true);
    setError(null);
    Promise.all([
      getMyCustomerProfile().catch(() => null),
      listMyCustomerInvoices({ limit: 50 }).then((r) => r.data ?? []).catch(() => [] as Invoice[]),
      listMyCustomerDeliveries({ limit: 10 }).catch(() => [] as DeliveryStop[]),
      listMyCustomerOrders({ limit: 50 }).then((r: any) => r.data ?? []).catch(() => [] as Order[]),
      listPromotions().catch(() => [] as Promotion[]),
      // customerId is ignored server-side for the Customer role (always
      // resolved to the caller) — see routes/orders.ts.
      getSuggestedReorder('me').catch(() => [] as SuggestedReorderLine[]),
    ])
      .then(([p, inv, st, ord, promo, sugg]) => {
        setProfile(p);
        setInvoices(inv);
        setStops(st);
        setOrders(ord);
        setPromotions(promo);
        setSuggestions(sugg);
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load dashboard'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const stats: CustomerStatData[] = useMemo(() => {
    const outstanding = invoices
      .filter((i) => i.status !== 'paid' && i.status !== 'cancelled')
      .reduce((sum, i) => sum + Number(i.total_amount || 0), 0);
    // VP-CUST-001: real credit_limit from the customer's own record
    // (users.credit_limit) instead of a hardcoded per-tier lookup table.
    const creditLimit = Number(profile?.credit_limit) || 0;
    const creditRemaining = Math.max(creditLimit - outstanding, 0);
    const pending = orders.filter((o: any) => o.status && !['delivered', 'cancelled', 'rejected'].includes(o.status)).length;
    const monthStart = new Date();
    monthStart.setDate(1);
    monthStart.setHours(0, 0, 0, 0);
    const monthly = orders
      .filter((o: any) => new Date(o.created_at) >= monthStart && o.status !== 'cancelled' && o.status !== 'rejected')
      .reduce((sum, o: any) => sum + Number(o.total_amount || 0), 0);

    return [
      { id: 'current-balance', title: 'Current Balance', value: fmtMoney(-outstanding), color: outstanding > 0 ? 'red' : 'green', icon: 'balance' },
      { id: 'credit-limit', title: 'Credit Limit Remaining', value: fmtMoney(creditRemaining), color: 'blue', icon: 'credit' },
      { id: 'pending-orders', title: 'Pending Orders', value: String(pending).padStart(2, '0'), color: 'orange', icon: 'orders' },
      { id: 'monthly-purchases', title: 'This Month Purchases', value: fmtMoney(monthly), color: 'green', icon: 'purchases' },
    ];
  }, [invoices, orders, profile]);

  const upcoming: DeliveryItem[] = useMemo(() =>
    stops
      .filter((s) => s.status === 'pending' || s.status === 'in_transit')
      .slice(0, 5)
      .map((s) => ({
        id: String(s.id),
        invoiceNumber: formatOrderId(s.order_id),
        dateLabel: s.scheduled_window_start ? relativeDate(s.scheduled_window_start) : 'Pending',
        status: s.status === 'in_transit' ? 'In Transit' : 'Scheduled',
      }))
  , [stops]);

  const outstandingInvoices: InvoiceItem[] = useMemo(() =>
    invoices
      .filter((i) => i.status !== 'paid' && i.status !== 'cancelled')
      .slice(0, 5)
      .map((i) => ({
        id: String(i.id),
        invoiceNumber: i.invoice_number,
        dueDate: `Due: ${new Date(new Date(i.invoice_date).getTime() + 30 * 86_400_000).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`,
        amount: fmtMoney(Number(i.total_amount || 0)),
      }))
  , [invoices]);

  // Pre-existing bug found while wiring up VP-CUST-001: this used to derive
  // suggestions by counting `order.items` from listMyCustomerOrders — but
  // GET /customers/me/orders only returns an `item_count` per order (no real
  // line items), so `items` was always a list of empty objects and this
  // always produced zero suggestions (and the "Add to Cart" button here had
  // no handler at all). Fixed to use the real per-SKU aggregation the
  // suggested-reorder endpoint already provides (same data source Sales Rep's
  // AI Insights suggested-reorder feature uses).
  const reorderProducts: ReorderProduct[] = useMemo(() =>
    suggestions.slice(0, 6).map((s) => ({
      id: String(s.sku_id),
      skuId: s.sku_id,
      name: s.product_name || s.sku_code,
      image: '',
      suggestedQuantity: s.suggested_quantity || 1,
    }))
  , [suggestions]);

  const handleAddSuggestionToCart = async (skuId: number, quantity: number) => {
    try {
      const sku = await getSku(skuId);
      addToCart(
        {
          sku_id: sku.id,
          sku_code: sku.sku_code,
          name: sku.product_name,
          unit_price: sku.customer_price ?? sku.list_price ?? sku.unit_cost,
          image_url: sku.image_url,
        },
        quantity
      );
      setReorderToast(`Added ${sku.product_name} to cart`);
      window.setTimeout(() => setReorderToast(null), 1600);
    } catch (err) {
      setReorderToast(err instanceof Error ? err.message : 'Failed to add to cart');
      window.setTimeout(() => setReorderToast(null), 2400);
    }
  };

  const activeDeals: DealItem[] = useMemo(() =>
    promotions
      .filter((p) => p.is_active && new Date(p.ends_at).getTime() > Date.now() && new Date(p.starts_at).getTime() <= Date.now())
      .slice(0, 6)
      .map((p) => ({
        id: String(p.id),
        title: p.name,
        discount: p.discount_type === 'percent' ? `${p.discount_value}% Off` : `$${p.discount_value} Off`,
        condition: p.scope === 'all' ? 'All products' : p.category_name || p.sku_code || 'Selected items',
        expires: `Expires: ${new Date(p.ends_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`,
      }))
  , [promotions]);

  return (
    <div className={styles.container}>
      <AsyncBoundary
        loading={loading && !profile}
        error={error}
        empty={false}
        onRetry={load}
      >
        <div className={styles.statsGrid}>
          {stats.map((stat) => (
            <CustomerStatCard
              key={stat.id}
              title={stat.title}
              value={stat.value}
              icon={stat.icon}
              color={stat.color}
            />
          ))}
        </div>

        <div className={styles.middleRow}>
          <div className={styles.quickActionsCol}>
            <QuickActions />
          </div>
          <div className={styles.deliveriesCol}>
            <UpcomingDeliveries deliveries={upcoming} />
          </div>
          <div className={styles.invoicesCol}>
            <OutstandingInvoices invoices={outstandingInvoices} />
          </div>
        </div>

        <AIReorderSuggestions products={reorderProducts} onAddToCart={handleAddSuggestionToCart} />
        <ActiveDeals deals={activeDeals} />
      </AsyncBoundary>
      {reorderToast && <div className={styles.toast}>{reorderToast}</div>}
    </div>
  );
};

export const CustomerDashboardPage: React.FC = () => {
  return <CustomerDashboard />;
};

export default CustomerDashboard;
